import { LockerRewardType } from "./locker";
import { PowerUpTypes } from "./subscriptions";
import { DiscountCategory } from "./discounts";

export interface MobileVersionRelease {
  docId: string;
  version: string;
  nativeUpdateRequired: boolean;
  android_native_required?: boolean;
  ios_native_required?: boolean;
  isApproved?: boolean;
  backwardsCompatible?: boolean;
  createdDt: number;
  updatedDt?: number;
  updatedBy: string;
}

export type LevelPrizeType = "entries" | "merch";
export interface Merchandise {
  name: string;
  //! this is temp
}
export interface LevelRewardsMatrix {
  docId: string;
  level: number;
  name: string;
  pointsToAchieve: number;
  prizeType: LevelPrizeType;
  prize: number | Merchandise;
  createdDt: number;
  updatedDt: number;
}

export type LevelRewardsMatrixCreate = Omit<
  LevelRewardsMatrix,
  "docId" | "createdDt" | "updatedDt"
>;

type MomentFrequency = "day" | "date" | "minute";
export interface LockerConfig {
  momentFrequency: MomentFrequency; // day | week moment(new Date()).get("day")
  momentFrequencyEvery: number;
}

export type ReferralBonusType = 1 | 2 | 3;

export const ReferralBonusTypeSourceMap = {
  1: "Bonus Power Up",
  2: "Bonus Entries",
  3: "Entries Multiplier", //! will only apply to sign ups (referred user)
};

export type ReferralSourceType = {
  [key: string]: {
    id: ReferralBonusType;
    name: string;
  };
};

export const REFERRAL_SOURCE_CONFIG: ReferralSourceType = {
  bonusPowerUps: { id: 1, name: "Bonus Power Ups" },
  bonusEntries: { id: 2, name: "Bonus Entries" },
  entriesMultiplier: { id: 3, name: "Entries Multiplier" },
} satisfies ReferralSourceType;

export interface ReferralPowerUpRewardsType {
  [key: number]: {
    title: string;
    id: PowerUpTypes;
    value: number;
    entries: number;
  };
}

export const ReferralPowerUpTypeSourceMap: ReferralPowerUpRewardsType = {
  1: {
    id: 1,
    title: "$25 Power Up",
    value: 25,
    entries: 2,
  },
  2: {
    id: 2,
    title: "$50 Power Up",
    value: 50,
    entries: 5,
  },
  3: {
    id: 3,
    title: "$100 Power Up",
    value: 100,
    entries: 20,
  },
  4: {
    id: 4,
    title: "$250 Power Up",
    value: 250,
    entries: 60,
  },
  5: {
    id: 5,
    title: "$500 Power Up",
    value: 500,
    entries: 150,
  },
} satisfies ReferralPowerUpRewardsType;

export type ReferralBonusValueType = PowerUpTypes | number;

export interface ReferralBonus {
  type: ReferralBonusType;
  value: ReferralBonusValueType;
}

export interface ReferralConfig {
  twoWayBonus: ReferralBonus;
  referrerBonus: ReferralBonus;
  referredBonus: ReferralBonus;

  isDefault: boolean;
  lookUpKey: string;
}

export interface BillingIssuePromoConfig {
  [attempt: number]: {
    [subscriptionId: string]: string; // promo.code
  };
}

export interface DefaultShopPromoConfig {
  starter: string | null;
  pro: string | null;
  mvp: string | null;
  legend: string | null;
  vip: string | null;
  club: string | null;
}

/**
 * Nested under `wlConfig/{docId}.discountDisplayRotation`. Drives the
 * weekly Mon 05:00 Brisbane left-rotation of category sections + per-category
 * non-featured tile order. The `rotateWeeklyDiscountDisplay` cron is the
 * sole writer; AppUser + Admin are read-only consumers via
 * `Common/services/discountDisplayConfig.resolveCategoryOrder`.
 *
 * Optional on `WLConfig` so existing wlConfig docs without the field don't
 * break clients. The cron seeds it on first run; before then,
 * `resolveCategoryOrder` falls back to `DISCOUNT_CATEGORIES_ROTATABLE`
 * (and always appends `DISCOUNT_CATEGORIES_LEGACY` at the bottom).
 *
 * Storage contract: `categoryOrder` holds **only** the rotatable subset
 * (current category ids 6–19). Legacy ids (1–5) are excluded from rotation
 * and never live in the persisted ring — `resolveCategoryOrder` re-appends
 * them in numeric-id order at the bottom of the rendered page at read time.
 */
export interface WLDiscountDisplayConfig {
  /** Permutation of the rotatable ring — current category ids (6–19). Index 0 = top of page. Legacy ids (1–5) never appear here. */
  categoryOrder: DiscountCategory[];
  /** Brisbane ISO week key (e.g. `"2026-W19"`) of the last successful rotation; `null` before first run. */
  lastRotationWeekKey: string | null;
  /** Epoch ms when the last successful rotation completed; `null` before first run. */
  lastRotatedAt: number | null;
}

export type LockerDays = 1 | 2 | 3 | 4 | 5;
export interface WLConfig {
  docId: string;
  locker: LockerConfig;
  showPowerUpDoubleFor50OffOffer: boolean;
  hardCodedRevenue: {
    updatedDt: number;
    revenue: number;
  };
  displayUpsell: boolean;
  respinCost: number;
  cancelFlowDiscount: number;
  firstUpgradeOfferCode: string;
  secondUpgradeOfferCode: string;
  thirdUpgradeOfferStarterCode: string;
  thirdUpgradeOfferPROCode: string;
  thirdUpgradeOfferMVPCode: string;

  oneTimePowerUpOffer: string;
  defaultPowerUpOffer: string;
  fourthFailedPaymentOffer: string;
  sixthFailedPaymentOffer: string;
  lastFailedPaymentOfferStarter: string;
  lastFailedPaymentOfferPro: string;
  lastFailedPaymentOfferMVP: string;
  lastFailedPaymentOfferLegend: string;
  lastFailedPaymentOfferVIP: string;
  reactivateAccountOffer: string;
  reactivateOfferExpirationDt: number;
  // powerUpOfferOnCancel: string;
  createdDt: number;
  updatedDt: number;
  updatedBy: string;
  updatedByEmail: string;
  referralConfig: ReferralConfig[];
  lockerRewardsList: RewardListConfig[];
  lockerRewardListDaily: {
    [key in LockerDays]: RewardListConfig[];
  };
  billingIssuePromoConfig: BillingIssuePromoConfig;
  defaultShopPromo?: DefaultShopPromoConfig;

  enableCapawesomeLiveUpdates: boolean;
  featureNextGiveawayInSignUp: boolean;
  enablePowerUpsPurchase: boolean;
  /** When true, AppUser shows Games FAB, routes, and loads game season/profile data. */
  enableGames?: boolean;
  /** When false, Stripe AppUser users see discounts-only. Omitted = Winners Shop enabled. */
  enableWinnersShop?: boolean;
  /** When true, an activated Winners Shop is only visible to excluded (test) users; everyone else sees it as inactive. Frontend-gated only. */
  winnersShopTestMode?: boolean;
  /** Optional — written only by the weekly discount-display rotation cron. */
  discountDisplayRotation?: WLDiscountDisplayConfig;
}

export type WLConfigCreate = Omit<
  WLConfig,
  "docId" | "createdDt" | "updatedDt" | "hardCodedRevenue" | "displayUpsell"
>;

export interface WLWhiteList {
  userEmails: string[];
  ignoreStripeHooks: string[];
}

export interface UserErrors {
  docId: string;
  userId: string;
  build: string;
  device: string;
  platform: string;
  url: string;
  error: string;
  firstEncounteredDt: number;
  createdDt: number;
}

export interface CreateUserErrors
  extends Omit<UserErrors, "docId" | "createdDt"> {}

export interface WLCreateMetaData {
  createdDt: number;
  createdBy: string;
}

export interface WLUpdateMetaData {
  updatedDt: number;
  updatedBy: string;
}

export type WLMetaData = WLCreateMetaData & WLUpdateMetaData;

export type WLOmittedMetaData =
  | "docId"
  | "createdDt"
  | "updatedDt"
  | "createdBy"
  | "updatedBy";

export interface RewardListConfig {
  id: string;
  type: LockerRewardType;
  amount: number;
  chance: number;
  powerUpType?: PowerUpTypes;
}

export type RewardListConfigCreate = Omit<RewardListConfig, "docId">;

export type WLAbMetadata = Record<string, unknown>;
export interface WLABTesting<T extends WLAbMetadata = WLAbMetadata> {
  gtId: string;
  identifier: string;
  revenue?: number | null;
  converted: boolean;
  createdDt: number;
  updatedDt?: number;
  metadata?: T;
}

export type WLABtestTypes = "ViewConversionRevenue";
export interface WLAbTestConfigIds {
  gtId: string;
  details: string;
  createdDt: number;
  archived: boolean;
  type: WLABtestTypes;
  docId?: string;
  remarks?: string;
  updatedDt?: number;
}

export type ABTestIdCreate = Omit<
  WLAbTestConfigIds,
  "docId" | "createdDt" | "updatedDt"
>;

export interface ABTestPreview {
  docId?: string;
  testName: string;
  previewWebUrl?: string;
  androidPreviewUrl?: string;
  iosPreviewUrl?: string;
  createdBy?: string;
  createdByEmail?: string;
  createdDt: number;
  updatedBy?: string;
  updatedByEmail?: string;
  updatedDt: number;
}

export type WLAbTestPayload<T extends WLAbMetadata = WLAbMetadata> = Omit<
  WLABTesting<T>,
  "converted" | "createdDt" | "updatedDt"
>;

// API Key Types
export type WLApiKeyTypes = "shopify" | "html5-game-drift" | "revenuecat";

export interface WLApiKey {
  wlKey: string;
  type: WLApiKeyTypes;
  details: string;
  createdDt: number;
  archived: boolean;
  docId?: string;
  remarks?: string;
  updatedDt?: number;
}

export type ApiKeyCreate = Omit<WLApiKey, "docId" | "createdDt" | "updatedDt">;
